import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import LogoutIcon from '@mui/icons-material/Logout';
import MicIcon from '@mui/icons-material/Mic';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import Tooltip from '@mui/material/Tooltip';
import { green, grey, red, yellow } from '@mui/material/colors';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ORDER_STATUS_ICON_RESET_INTERVAL } from '../../constants';
import { messagingActions } from '../../reducers/messagingSlice';
import { resetCurrentTransactionId } from '../../reducers/orderSlice';
import {
  OrderStatus,
  TransactionStatus,
} from '../../reducers/orderSlice.constants';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagOrderStatusBanner,
  selectFeatureFlagTaskRouter,
  selectIsShadowModeEnabled,
} from '../../redux/features/config/config.selector';
import { SessionStatus } from '../../redux/features/sessionBoundary/sessionBoundary.constants';
import {
  selectSessionId,
  selectSessionStatus,
} from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectIsWebSocketConnected } from '../../selectors/message';
import { activeCartSelector } from '../../utils/cart';
import logger from '../../utils/logger';
import {
  selectCurrentTransactionId,
  selectOrderError,
  selectTransactions,
} from '../../selectors/order';
import { Player } from '../Audio/Player';
import { VisualIndicator } from '../VisualIndicator';
import HealthStatusCheckIcon from '../VisualIndicator/HealthStatusCheckIcon';
import { Timer } from '../VisualIndicator/Timer';
import ConfirmDialog from '../common/ConfirmDialog';
import { setIsKillSwitchClicked } from '../../redux/features/taskRouter/taskRouter.slice';
import { ExpressUpsellIndicator } from './ExpressUpsellIndicator';
import { LocalTimeIndicator } from './LocalTimeIndicator';
import KeyboardShortcutsHelp from '../common/KeyboardShortcutsHelp';
import useExpressTimerLatch from '../../hooks/useExpressTimerLatch.hooks';

const HeaderIndicators: FC = () => {
  const dispatch = useAppDispatch();
  // hitl_3_0 moves the express countdown into the Pulse "Menu Board Time" tile
  // and drops the header <Timer/>. This headless hook keeps the express-mode
  // latch that the Timer used to own (inert in the legacy UX).
  useExpressTimerLatch();
  const isCarPresent = useAppSelector((state) => state.messages.isCarPresent);
  const isNewSession = useAppSelector((state) => state.messages.isNewSession);
  const isConnected = useAppSelector(selectIsWebSocketConnected);
  // HITL-1609: with Supervised AI mode on, the clock moves out of this row and
  // onto a second line under the menu version in Header.tsx, returning ~90px to
  // a bar that now also carries the agent-mode switch. Rendered in exactly one
  // of the two places, never both. Flag off = it stays here, as today.
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);
  const sessionStatus = useAppSelector(selectSessionStatus);
  const isStaffIntervention = useAppSelector(
    (state) => state.messages.isStaffIntervention
  );
  const [orderStatus, setOrderStatus] = useState(OrderStatus.noOrder);
  const sendStaffInterventionMetric = () => {
    //TODO
  };
  const isTTSOn = useAppSelector((state) => state.messages.isTTSOn);
  const currentTransactionId = useAppSelector(selectCurrentTransactionId);
  const transactions = useAppSelector(selectTransactions);
  const orderError = useAppSelector(selectOrderError);
  const transaction = currentTransactionId
    ? transactions[currentTransactionId]
    : null;
  useEffect(() => {
    if (orderError) {
      setOrderStatus(OrderStatus.failed);
    } else if (!transaction) {
      setOrderStatus(OrderStatus.noOrder);
    } else {
      if (
        transaction?.isFinal &&
        transaction.status === TransactionStatus.completed
      ) {
        setOrderStatus(OrderStatus.completed);
        setTimeout(() => {
          dispatch(resetCurrentTransactionId());
        }, ORDER_STATUS_ICON_RESET_INTERVAL);
      } else if (
        transaction.status === TransactionStatus.pending ||
        transaction.status === TransactionStatus.completed
      ) {
        setOrderStatus(OrderStatus.pending);
      } else if (transaction.status === TransactionStatus.error) {
        setOrderStatus(OrderStatus.failed);
      } else {
        setOrderStatus(OrderStatus.noOrder);
      }
    }
  }, [transaction, transaction?.status, orderError]);

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case OrderStatus.noOrder:
        return grey['500'];
      case OrderStatus.completed:
        return green['A400'];
      case OrderStatus.pending:
        return yellow['500'];
      case OrderStatus.failed:
        return red['700'];
      default:
        return '';
    }
  };

  useEffect(() => {
    //TTS request being sent when hitl session is started
    if (isConnected && sessionStatus === SessionStatus.start) {
      dispatch(messagingActions.sendTTSRequest({} as any));
    }
  }, [isConnected, sessionStatus, dispatch]);

  // HITL-1563: the kill switch must never abandon an unsubmitted order, so it
  // is disabled while the cart holds ANYTHING — agent items or AI hypothesis
  // items. Keyed on the CART, deliberately not on `finalOrderSubmitted` or the
  // staff-intervention signals: `finalOrderSubmitted` is transient (the
  // post-final reset in OrderActions flips it back within milliseconds), and
  // `restaurantStaffIntervention` never returns to `initial` for the rest of
  // the shift (see the latch discussion in OrderActions) — either key enables
  // the button at the wrong moments. The empty cart is the durable consequence
  // of every settled path (finish click, AI final, staff intervention, timer
  // auto-submit: all clear the cart), and an orderless stuck session has an
  // empty cart, so the escape hatch keeps working exactly when agents need it.
  // Click behaviour is untouched — the enabled button only ends the session.
  const isKillSwitchEnabled = !useAppSelector(activeCartSelector);

  const [showEndSessionConfirmDialog, setShowEndSessionConfirmDialog] =
    useState(false);

  // The confirm dialog is PART of the gate (review round 1): opened while the
  // cart was empty, it must not survive the cart filling — a car can start
  // ordering while the dialog sits open, and its Confirm would then abandon
  // exactly the order the disabled button protects.
  useEffect(() => {
    if (!isKillSwitchEnabled) setShowEndSessionConfirmDialog(false);
  }, [isKillSwitchEnabled]);

  const handleEndSession = () => {
    setShowEndSessionConfirmDialog(false);
    // Same-tick belt for the effect above: a Confirm click racing the cart
    // update must not end a session that now holds an order.
    if (!isKillSwitchEnabled) return;
    dispatch(setIsKillSwitchClicked(true));
  };
  const taskRouterFeatureFlag = useAppSelector(selectFeatureFlagTaskRouter);
  const sessionId = useAppSelector(selectSessionId);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isOrderStatusBannerEnabled = useAppSelector(
    selectFeatureFlagOrderStatusBanner
  );
  const currentTaskId = useAppSelector(selectCurrentTask);

  // KILL_SWITCH_GATE (HITL-1563): one log per TRANSITION, only while the
  // button is actually rendered (task-router task active) — never per render.
  // This is the gate's telemetry: how often agents face a disabled kill
  // switch, and the trail for a "kill switch greyed out" report. The first
  // evaluation after the button appears logs too, so every task records the
  // state the button started in. Flat payload — stringifyWithDepth truncates
  // objects at depth 2 (HITL-1551-1).
  const isKillSwitchRendered = Boolean(taskRouterFeatureFlag && currentTaskId);
  // Keyed on session AND state (review round 1, #4): dedupe by state alone
  // meant a session transition with the gate unchanged logged nothing, so a
  // "greyed out" investigation read the PREVIOUS session's id off the latest
  // line. Every session now logs its state at least once.
  const prevKillSwitchGateKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isKillSwitchRendered) {
      // Reset so the next task logs its starting state again.
      prevKillSwitchGateKey.current = null;
      return;
    }
    const gateKey = `${sessionId}|${isKillSwitchEnabled}`;
    if (prevKillSwitchGateKey.current === gateKey) return;
    prevKillSwitchGateKey.current = gateKey;
    logger.info({
      message: `KILL_SWITCH_GATE: ${
        isKillSwitchEnabled ? 'enabled' : 'disabled'
      }`,
      killSwitchEnabled: isKillSwitchEnabled,
      sessionId,
    });
  }, [isKillSwitchRendered, isKillSwitchEnabled, sessionId]);

  return (
    // One flex unit that never shrinks, so the indicators keep their size (and
    // the local time never wraps) — only the title/sessionId on the left gives
    // up space when the header is tight.
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        // Uniform spacing between every indicator (buttons, spans, divs alike);
        // per-item margins are removed so this gap is the single source of
        // spacing.
        gap: '4px',
      }}
    >
      {taskRouterFeatureFlag && currentTaskId ? (
        <>
          {/* A disabled MUI button swallows hover, so the span hosts the
              tooltip. Title is '' when enabled — MUI renders no tooltip. */}
          <Tooltip
            title={
              isKillSwitchEnabled
                ? ''
                : 'Finish or cancel the order to end the session'
            }
            placement="bottom"
            describeChild
            disableHoverListener={isKillSwitchEnabled}
            disableFocusListener={isKillSwitchEnabled}
            disableTouchListener={isKillSwitchEnabled}
          >
            <span>
              <VisualIndicator
                onClick={() => setShowEndSessionConfirmDialog(true)}
                IconComponent={LogoutIcon}
                isActive={isKillSwitchEnabled}
                disabled={!isKillSwitchEnabled}
                data-testid="kill-switch"
                aria-label="End session"
              />
            </span>
          </Tooltip>
          {showEndSessionConfirmDialog && (
            <ConfirmDialog
              open={showEndSessionConfirmDialog}
              content="Do you want to exit the session?"
              onClose={() => setShowEndSessionConfirmDialog(false)}
              onConfirm={handleEndSession}
            />
          )}
        </>
      ) : null}
      {!isShadowModeEnabled && <LocalTimeIndicator />}
      <HealthStatusCheckIcon />
      <Player />
      <VisualIndicator IconComponent={MicIcon} isActive={isTTSOn} />
      <VisualIndicator
        IconComponent={NetworkCheckIcon}
        isActive={isConnected}
      />
      <VisualIndicator
        IconComponent={HeadsetMicIcon}
        isActive={isStaffIntervention}
        onClick={sendStaffInterventionMetric}
      />
      <VisualIndicator
        IconComponent={DirectionsCarIcon}
        isActive={isCarPresent || isNewSession}
      />
      {/* HITL-1527: the Order Status Banner carries the order state (final
          included), so the header's dollar-sign indicator retires with it. */}
      {!isOrderStatusBannerEnabled && (
        <VisualIndicator
          IconComponent={AttachMoneyIcon}
          customizedColor={getOrderStatusColor(orderStatus)}
        />
      )}
      {/* hitl_3_0 surfaces express mode as a Pulse "Signals" row and the
          countdown as the Pulse "Menu Board Time" tile, so the header no longer
          shows the express bolt or the timer. Legacy keeps both. */}
      {!isHitl3 && <ExpressUpsellIndicator />}
      {isHitl3 && <KeyboardShortcutsHelp />}
      {!isHitl3 && <Timer />}
    </div>
  );
};

export default HeaderIndicators;
